import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { Field, Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import { Button, CircularProgress, Grid, TextField } from '@material-ui/core';

import notification, { closeToast, toastIds } from 'app/config/notification';
import helpers, { labelObrigatoriedade } from 'app/utils/helpers';
import { clearFiles, setBoExcluidoByIndex, setFiles, setFilesByIndex } from 'app/store/slices/forms';

import requestEndpoints from 'app/api/services/requestEndpoints';
import vehiclesEndpoint from 'app/api/services/vehiclesEndpoint';

import DocumentField from 'app/components/DocumentField';
import GrowingTable from 'app/components/GrowingTable';
import SaveButton from '../SaveButton';
import AdditionalData from './AdditionalData';
import AdditionalFiles from './AdditionalFiles';
import VehicleData from './VehicleData/VehicleData';
import OwnerOperator from './OwnerOperator';

import { SERVICES_CODES, SERVICES_GROUPS, WITH_DETRAN } from 'app/config/services';
import {
    addRequiredFields,
    camposObrigatoriosPadraoVehicleForm,
    surveyValidationSchema,
    validacoesVehicleForm,
} from 'app/utils/forms/validationsSchemas';
import { STATUS_CODES } from 'app/config/status';
import { DOCUMENT_TYPES } from 'app/utils/constants';
import { vehiclePayload } from 'app/utils/forms/payloadsStruture';

import { handleChangeFile, handleDeleteFile } from 'app/utils/forms/files';
import { fixedDocumentName } from 'app/utils/forms/validators/file';
import { validateDocumentation, validateFormikForm } from 'app/utils/forms/functions';
import { validateVehicle, validateVehicleAge } from 'app/utils/forms/validators/vehicle';
import { updateRequestDetails } from 'app/store/slices/requests';
import { DETRAN_KEYS } from 'app/store/slices/vehicle';
import HighlightableTextField from './HighlightableTextField';
import ImperativeFormValues from './ImperativeFormValues';
import companyEndpoints from 'app/api/services/companyEndpoints';
import { validaInclusaoVeiculoComplementar } from 'app/utils/forms/validators/company';
import AdditionalDataSurvey from './AdditionalDataSurvey';

function VehicleForm(props) {
    const dispatch = useDispatch();

    const {
        intl,
        readOnly,
        vehicle,
        classes,
        onSave,
        onCancel,
        onCloseDrawer,
        vehiclesLimit,
        contextProps: { isInternalForm, canEdit, mainColor },
    } = props;

    const activeCompany = useSelector((state) => state.company.activeCompany);
    const registrationUserInfo = useSelector((state) => state.auth.registrationInfo);

    const revisions = useSelector((state) => state.revisions.revisions);
    const vehicles = useSelector((state) => state.forms.vehicles);
    const documents = useSelector((state) => state.forms.files);
    const subSystems = useSelector((state) => state.vehicle.subSystems);
    const vehicleCategories = useSelector((state) => state.vehicle.vehicleCategories);
    const fuelType = useSelector((state) => state.vehicle.fuelType);
    const accessibilityType = useSelector((state) => state.vehicle.accessibilityType);
    const vehicleClassification = useSelector((state) => state.vehicle.vehicleClassification);
    const vehicleType = useSelector((state) => state.vehicle.vehicleType);
    const vehicleModels = useSelector((state) => state.vehicle.vehicleModels);
    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);
    const internalNewRequest = useSelector((state) => state.forms.internalNewRequest);
    const vehicleDocuments = useSelector((state) => state.vehicle.licenseVehicleDocuments);

    const imperativeFormControlsRef = useRef({});

    const isAlteracaoVeiculo = useMemo(() => {
        return (
            service.cdServico === SERVICES_CODES.ALTERACAO_VEICULOS ||
            service.cdServico === SERVICES_CODES.ALTERACAO_VEICULOS_SEM_CRC
        );
    }, [service]);

    const isInclusaoVeiculo = useMemo(() => {
        return (
            service.cdServico === SERVICES_CODES.INCLUSAO_VEICULOS ||
            service.cdServico === SERVICES_CODES.INCLUSAO_VEICULOS_SEM_CRC
        );
    }, [service]);

    const isSurvey = useMemo(() => {
        return ((service.cdServico === SERVICES_CODES.VISTORIA) ||
            (internalNewRequest.servico?.cdServico === SERVICES_CODES.VISTORIA));
    }, [service, internalNewRequest.servico]);

    const isSituacaoAnalise = useMemo(() => {
        return request.cdSituacao === STATUS_CODES.EM_ANALISE;
    }, [request]);

    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingDetran, setLoadingDetran] = useState(false);
    const [foundDetran, setFoundDetran] = useState(false);
    const [foundOnDetran, setFoundOnDetran] = useState([]);
    const [uploading, setUpLoading] = useState(false);
    const [loadingCRLV, setLoadingCRLV] = useState(false);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [editing, setEditing] = useState(false);

    const [othersDocuments, setOthersDocuments] = useState([]);
    const [hideAditionalDocs, setHideAditionalDocs] = useState(false);

    const [vehiclesInitialValues, setVehiclesInitialValues] = useState(vehiclePayload);
    const [vehicleOriginalValues, setVehicleOriginalValues] = useState(vehicle?.content?.dadosOriginais);
    const [highlightEnabled, setHighlightEnabled] = useState(false);
    const [requiredFields, setRequiredFields] = useState(camposObrigatoriosPadraoVehicleForm);
    const [formMetadata, setFormMetadata] = useState(vehicle?.content?.metadata ?? {});

    const [idFile, setIdFile] = useState(null);
    const [idGdoc, setIdGdoc] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [CRLVAlterada, setCRLVAlterada] = useState(false);

    const [documentsList, setDocumentsList] = useState([]);

    const [company, setCompany] = useState({});

    useEffect(() => {
        if (Object.keys(company).length > 0) return;
        if (Object.keys(activeCompany).length > 0) setCompany(activeCompany);
        else setCompany(internalNewRequest.company);
        // eslint-disable-next-line
    }, [activeCompany, internalNewRequest.company]);

    const [canEditField] = useState(() => {
        if (service.cdServico === SERVICES_CODES.VISTORIA) {
            return request.cdSituacao === STATUS_CODES.EM_ANALISE;
        }
        if (
            service.cdServico === SERVICES_CODES.EXCLUSAO_VEICULOS ||
            readOnly ||
            SERVICES_GROUPS.LICENSE.includes(service.cdServico)
        ) {
            return false;
        }
        return canEdit;
    });

    // definição _principal_ de disabled. (alguns campos possuem regras extras)
    const isFieldDisabled = useCallback(
        (name) => {
            const disabled = (name) => {
                switch (
                    name // alguns dos que utilizam regras extras precisam de defaults distintos
                ) {
                    case 'nmOperador':
                    case 'nuAgerba':
                        return true;
                    case 'nuAnoIpva':
                    case 'municipio':
                        return false;
                    default:
                        return !canEditField;
                }
            };

            if (isAlteracaoVeiculo) {
                return formMetadata?.disabled?.[name] ?? disabled(name);
            }

            return disabled(name);
        },
        [formMetadata, canEditField, isAlteracaoVeiculo],
    );

    const [blockChangesField] = useState(() => {
        if (readOnly) return true;
        if (isAlteracaoVeiculo && !isInternalForm && !canEditField) {
            return true;
        }
        return !canEditField;
    });

    const [showSaveButton] = useState(() => {
        if (readOnly) return false;
        if (isInternalForm) return canEditField;
        if (
            service.cdServico === SERVICES_CODES.EXCLUSAO_VEICULOS ||
            service.cdServico === SERVICES_CODES.VISTORIA ||
            SERVICES_GROUPS.LICENSE.includes(service.cdServico)
        ) {
            if (!request.situacao) return true; // new request
            return request.situacao?.boEdicaoPortalExterno || request.cdSituacao === STATUS_CODES.EM_CONSTRUCAO;
        }
        return canEditField;
    });

    const [disableEditCRLV, setDisableEditCRLV] = useState(() => {
        if (readOnly) return true;
        return !showSaveButton;
    });

    const [disableOtherDocs] = useState(() => {
        if (readOnly) return true;
        return !showSaveButton;
    });

    const [indexActualVehicle] = useState(() => {
        if (vehicle && vehicle.index !== null && vehicle.index !== undefined) {
            setEditing(true);
            return vehicle.index;
        }
        return vehicles.length;
    });

    // currying para possibilitar o debouncing dos HighlightableXYZ
    const campoDiferenteDoOriginal = (nomeCampo) => (valor) => {
        if (!vehicleOriginalValues) {
            return false;
        }

        // caso especial necessário pra cpf/cnpj
        const nomeCampoLowerCase = nomeCampo?.toLowerCase();
        const campoCpfOuCnpj = nomeCampoLowerCase?.includes('cpf') || nomeCampoLowerCase?.includes('cnpj');
        if (campoCpfOuCnpj) {
            valor = helpers.somenteAlfanumericos(valor);
        }

        // existem propriedades aninhadas...
        const campoOriginal = nomeCampo.split('.').reduce((acc, cur) => acc?.[cur], vehicleOriginalValues);
        if (campoOriginal == null && !valor?.toString()) {
            return false;
        }
        return valor?.toString() !== campoOriginal?.toString();
    };

    const detranSearch = async (value) => {
        const result = {};
        const foundOnDetranInt = [];
        const vehiclesDetranData = await vehiclesEndpoint.getVehicleInfosDetran(value);
        if (vehiclesDetranData && vehiclesDetranData.status === 200 && vehiclesDetranData.data) {
            const responseData = vehiclesDetranData.data;
            if (Object.keys(responseData).length > 0) {
                setFoundDetran(true);
                DETRAN_KEYS.forEach((key) => {
                    if (responseData[key]) {
                        result[key] = responseData[key];
                        if (key === 'modelo') {
                            foundOnDetranInt.push('cdMarca');
                            foundOnDetranInt.push('cdModeloVeiculo');
                            result['cdMarca'] = responseData[key].cdMarca;
                            result['cdModeloVeiculo'] = responseData[key].cdModeloVeiculo;
                        } else if (key === 'municipio') {
                            if (responseData[key].uf) {
                                foundOnDetranInt.push('uf');
                                result['uf'] = responseData[key].uf.sgUf;
                            }
                            foundOnDetranInt.push('cdMunicipio');
                            result['cdMunicipio'] = responseData[key].cdMunicipio;
                        } else {
                            foundOnDetranInt.push(key);
                        }
                    } else {
                        result[key] = '';
                    }
                });
            }
        } else {
            setFoundDetran(false);
        }
        setFoundOnDetran(foundOnDetranInt);
        result.foundOnDetran = foundOnDetranInt;
        return result;
    };

    const consumirDadosAtualizadosDETRAN = ({ dadosAtualizados, dadosOriginais }) => {
        const formValues = { ...(vehicle?.content || dadosOriginais || vehiclesInitialValues) };

        for (let key in dadosAtualizados) {
            formValues[key] = dadosAtualizados[key];
            imperativeFormControlsRef.current.setFieldValue(key, dadosAtualizados[key]);
        }

        setLoadingDetran(true);
        setTimeout(() => {
            // necessário pra dependentes do loadingDetran funcionarem certinho...
            setVehiclesInitialValues(formValues);
            setLoadingDetran(false);
        }, 0);
    };

    // highlightEnabled
    useEffect(() => {
        setHighlightEnabled(!loadingDetran && isAlteracaoVeiculo);
    }, [loadingDetran, isAlteracaoVeiculo]);

    // inicialização do vehiclesInitialValues
    useEffect(() => {
        (async () => {
            setLoading(true);

            const formValues = { ...vehiclePayload, ...vehicle?.content };

            if (formValues.dadosOriginais != null) {
                setVehicleOriginalValues(formValues.dadosOriginais);
            }

            const devePopularUF = !formValues.uf && formValues.municipio?.uf;
            if (devePopularUF) {
                formValues.uf = formValues.municipio?.uf?.sgUf;
            }

            const devePopularCdModeloVeiculo = formValues.cdModelo && !formValues.cdModeloVeiculo;
            if (devePopularCdModeloVeiculo) {
                formValues.cdModeloVeiculo = formValues.cdModelo;
            }

            const devePopularCdMarca = !formValues.cdMarca && formValues.modelo?.cdMarca;
            if (devePopularCdMarca) {
                formValues.cdMarca = formValues.modelo.cdMarca;
            }

            const popularNmOperadorPeloFluxoBotaoNovo =
                internalNewRequest && Object.keys(internalNewRequest).length > 0;
            if (popularNmOperadorPeloFluxoBotaoNovo) {
                const baseData = { ...(internalNewRequest.company || internalNewRequest.user) };
                formValues.nmOperador = baseData.deRazaoSocial || baseData.nmCompleto || baseData.nmUsuario;
            } else {
                const devePopularNmOperador = !formValues.nmOperador || formValues.nmOperador === '';
                if (devePopularNmOperador) {
                    formValues.nmOperador = activeCompany?.deRazaoSocial || registrationUserInfo.nmUsuario;
                }
            }

            const possuiPlaca = formValues.dePlaca;
            const possuiRenavam = formValues.deRenavam;
            if (possuiPlaca || possuiRenavam) {
                setFoundDetran(true);
            }

            const possuiDocumentos = formValues.documentos && formValues.documentos.length > 0;
            if (possuiDocumentos) {
                const CRLV = formValues.documentos.filter((doc) => doc.cdTipoDocumento === DOCUMENT_TYPES.CRLV);
                const possuiCRLV = CRLV.length > 0;
                if (possuiCRLV) {
                    setIdFile(CRLV[0].idFile);
                    setIdGdoc(CRLV[0].idGdoc);
                    setFileName(CRLV[0].fileName);
                    setCRLVAlterada(CRLV[0].boAlterado);
                }
            }

            if (formValues.foundOnDetran) {
                // ? ... foundDetran significa que tem placa na verdade??
                setFoundOnDetran(formValues.foundOnDetran);
            }

            setVehiclesInitialValues(formValues);
        })();
    }, [activeCompany, internalNewRequest, registrationUserInfo.nmUsuario, vehicle, isInternalForm]);

    // integração com o DETRAN das solicitações de alteração de dados
    useEffect(() => {
        (async () => {
            if (!loading) {
                const formValues = { ...vehiclesInitialValues };

                if (isAlteracaoVeiculo && WITH_DETRAN) {
                    const realizarWorkaroundResetPosSave = vehicle?.content == null;
                    if (realizarWorkaroundResetPosSave) {
                        return;
                    }

                    setLoadingDetran(true);

                    const vehicleData = vehicle?.content ?? vehicle;
                    const obterDadosOriginais = vehicleData?.dadosOriginais == null;
                    let response = await vehiclesEndpoint.getDadosAtualizadosParaAlteracao({
                        placaChassiRenavam: vehicleData?.deRenavam,
                        obterDadosOriginais,
                    });

                    const successfulRequest = response?.status === 200;
                    if (successfulRequest) {
                        if (obterDadosOriginais) {
                            formValues.dadosOriginais = response.data.dadosOriginais;
                            setVehicleOriginalValues(formValues.dadosOriginais);
                            imperativeFormControlsRef.current.setFieldValue(
                                'dadosOriginais',
                                formValues.dadosOriginais,
                            );
                        }

                        setFormMetadata(response.data.metadata);
                        setRequiredFields(response.data.metadata.required);

                        const dadosAtualizados = response.data.dadosAtualizados;
                        const dadosOriginais = response.data.dadosOriginais;
                        let houveAtualizacaoDETRAN = false;
                        for (let key in dadosAtualizados) {
                            if (JSON.stringify(formValues[key]) !== JSON.stringify(dadosAtualizados[key])) {
                                houveAtualizacaoDETRAN = true;
                                break;
                            }
                        }
                        if (houveAtualizacaoDETRAN) {
                            if (isInternalForm && request?.cdSituacao === STATUS_CODES.EM_ANALISE) {
                                notification(
                                    <div>
                                        Foram encontradas atualizações deste veículo na base de dados do DETRAN. <br />{' '}
                                        <br />
                                        Essas informações serão apresentadas com uma cor de destaque no(s) campo(s)
                                        alterado(s). <br /> <br />
                                        Necessário acionar o 'Salvar' para confirmar essas atualizações. <br /> <br />
                                        Deseja atualizar? <br /> <br />
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: '4px',
                                                position: 'relative',
                                                left: '14px',
                                            }}>
                                            <Button
                                                color="primary"
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    consumirDadosAtualizadosDETRAN({ dadosAtualizados });
                                                    closeToast(toastIds.atualizacaoDETRAN);
                                                }}>
                                                OK
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="small"
                                                variant="contained"
                                                onClick={() => {
                                                    closeToast(toastIds.atualizacaoDETRAN);
                                                }}>
                                                Cancelar
                                            </Button>
                                        </div>
                                    </div>,
                                    {
                                        id: toastIds.atualizacaoDETRAN,
                                        type: 'info',
                                        closeOnClick: false,
                                        autoClose: false,
                                    },
                                );
                            } else {
                                consumirDadosAtualizadosDETRAN({ dadosAtualizados, dadosOriginais });

                                notification(
                                    <div>
                                        Foram encontradas atualizações deste veículo na base de dados do DETRAN. <br />{' '}
                                        <br />
                                        Essas informações serão apresentadas com uma cor de destaque no(s) campo(s)
                                        alterado(s). <br /> <br />
                                        Necessário acionar o 'Salvar' para confirmar essas atualizações.
                                    </div>,
                                    {
                                        id: toastIds.atualizacaoDETRAN,
                                        type: 'info',
                                    },
                                );
                            }
                        }
                    }

                    setLoadingDetran(false);

                    if (!successfulRequest) {
                        notification(
                            'Houve um problema ao realizar a integração com o DETRAN. Contate o administrador do sistema.',
                            { type: 'error' },
                        );
                    }
                }
            }
        })(); // eslint-disable-next-line
    }, [loading]); // como as outras dependências existem por hooks nativos, é garantido que os símbolos não vão se perder.

    const handleChangeAditionalFile = useCallback(
        async (docParam, index) => {
            if (docParam.id) {
                const file = {
                    idFile: docParam.id,
                    fileName: docParam.name,
                    cdTipoDocumento: DOCUMENT_TYPES.OTHERS,
                    canEdit: canEdit && !readOnly,
                };
                file.boExcluido = docParam.boExcluido || false;
                file.boAlterado = docParam.boAlterado || false;
                if (docParam.idGdoc) file.idGdoc = docParam.idGdoc;
                await dispatch(setFilesByIndex({ data: file, index }));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, canEdit],
    );

    const handleDeleteAditionalFile = useCallback(
        async (idFile, index) => {
            await dispatch(setBoExcluidoByIndex({ index }));
        },
        [dispatch],
    );

    const [filesTableData, setFilesTableData] = useState({
        columns: [
            {
                title: 'Arquivo',
                field: 'fileName',
                align: 'left',
                showContent: (value, index) => {
                    return (
                        <>
                            <Field
                                {...props}
                                value={value.fileName}
                                required={true}
                                name={value?.fileName ? value.fileName : `documento-adicional-${index}`}
                                id={`${DOCUMENT_TYPES.OTHERS}-${index}`}
                                preffix={value?.dePlaca}
                                disabled={!value?.canEdit}
                                idGdoc={value?.idGdoc}
                                idFile={value?.idFile}
                                onChangeFile={(doc) => {
                                    handleChangeAditionalFile(doc, index);
                                    setUpLoading(false);
                                }}
                                onDeleteFile={(idFile) => handleDeleteAditionalFile(idFile, index)}
                                uploading={uploading}
                                setUpLoading={setUpLoading}
                                fullWidth
                                margin="dense"
                                component={DocumentField}
                            />
                        </>
                    );
                },
            },
            {
                title: 'Descrição',
                field: 'deDescricao',
                align: 'left',
                editComponent: (props) => (
                    <TextField
                        {...props}
                        margin="dense"
                        label=""
                        fullWidth
                        inputProps={{
                            maxLength: 90,
                        }}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        error={props.value === ''}
                        helperText={props.value === '' ? 'Campo obrigatório' : ''}
                    />
                ),
            },
        ],
        data: documents,
    });

    const addFiles = async () => {
        const tempDocuments = [...documents];
        tempDocuments.push({});
        setOthersDocuments(tempDocuments);
        await dispatch(setFiles(tempDocuments));
    };

    const onChangeField = (e, index) => {
        const name = e.target.name;
        const value = e.target.value;
        const temp = _.cloneDeep(documents);
        if (temp[index]) {
            temp[index][name] = value;
            setOthersDocuments(temp);
        }
    };

    const onBlurField = async (e, index) => {
        const name = e.target.name;
        let value = e.target.value;

        if (name === 'deDescricao') {
            value = fixedDocumentName(value);
            const temp = _.cloneDeep(documents);
            if (temp[index]) {
                temp[index][name] = value;
                setOthersDocuments(temp);
            }
        }

        if (documents[index]) {
            const file = { ...documents[index] };
            file[name] = value;
            await dispatch(setFilesByIndex({ data: file, index }));
        }
    };

    const customAddDocuments = async (index) => {
        setLoadingDocuments(true);
        const tempDocuments = [...documents];

        let canEditDoc = canEdit;
        if (!canEditDoc && service.cdServico === SERVICES_CODES.EXCLUSAO_VEICULOS) {
            if (!request.situacao) {
                // new request
                canEditDoc = true;
            } else {
                canEditDoc =
                    request.situacao?.boEdicaoPortalExterno || request.cdSituacao === STATUS_CODES.EM_CONSTRUCAO;
            }
        }

        if (index === undefined) {
            tempDocuments.push({
                canEdit: canEditDoc,
                dePlaca: vehicle.content.dePlaca,
            });
        } else {
            if (window.confirm('Deseja excluir este item?')) {
                if (tempDocuments[index]?.idFile) {
                    await dispatch(setBoExcluidoByIndex({ index }));
                    tempDocuments.splice(index, 1);
                } else {
                    tempDocuments.splice(index, 1);
                }
            }
        }
        setOthersDocuments(tempDocuments);
        await dispatch(setFiles(tempDocuments));
        setLoadingDocuments(false);
    };

    const handleChangeField = (e, setFieldValue) => {
        const name = e.target.name;
        let value = e.target.value;
        if (name === 'dePlaca') {
            value = value.toUpperCase();
        } else if (name === 'deRenavam') {
            value = helpers.somenteNumeros(value);
        }
        setFieldValue(name, value);
    };

    const resetFiles = async (dePlaca, setFieldValue, modifiedValues) => {
        //TODO: rever aqui o que eh feito c/ vehiclesInitialValues
        let tempInitials = { ...vehiclesInitialValues };
        if (modifiedValues) tempInitials = { ...modifiedValues };
        // tempInitials.dePlaca = dePlaca;
        // tempInitials.documentos = [];
        // await setVehiclesInitialValues(tempInitials);
        setFoundDetran(false);
        if (!tempInitials.edited) {
            // await setFieldValue('documentos', []);
            setFieldValue('documentos', []);
            setIdFile(null);
            setIdGdoc(null);
            setFileName(null);
            setOthersDocuments([]);
            await dispatch(clearFiles());
        }
    };

    const getLastValue = (target) => {
        return target.getAttribute('data-last-value');
    };

    const setLastValue = (target) => {
        if (getLastValue(target) !== target.value) {
            target.setAttribute('data-last-value', target.value);
        }
    };

    const handleChangePlateAnaliseInclusao = async (target, setFieldValue, deRenavam, isFieldValid) => {
        if (!isFieldValid) {
            notification(`A placa informada (${target.value}) não é válida e não pode ser utilizada.`, {
                toastId: `PLATE_VALIDATION_${target.value}`,
                type: 'warning',
            });
            setFieldValue('dePlaca', getLastValue(target));
        } else {
            setLoadingDetran(true);
            await vehiclesEndpoint
                .getVehicleInfosDetran(target.value)
                .then((response) => {
                    const { data = {}, status } = response || {};
                    const dataLength = Object.keys(data).length;
                    if (!response || status !== 200 || dataLength === 0 || data.deRenavam !== deRenavam) {
                        notification(
                            `A placa informada (${target.value}) não corresponde ao veículo atual e não pode ser utilizada.`,
                            {
                                toastId: `PLATE_VALIDATION_${target.value}`,
                                type: 'warning',
                            },
                        );
                        setFieldValue('dePlaca', getLastValue(target));
                    }
                })
                .finally(() => setLoadingDetran(false));
        }
    };

    const validateFieldValue = async (name, value) => {
        try {
            const validationSchema = addRequiredFields(requiredFields, validacoesVehicleForm);
            await validationSchema.validateAt(name, { [name]: value });
        } catch (error) {
            // throw error.errors[0];
            return false;
        }
        return true;
    };

    const handleFocusField = ({ target }) => {
        setLastValue(target);
    };

    const handleBlurField = async ({ target }, setFieldValue, values) => {
        const { name, value } = target;

        if (value === getLastValue(target)) return;

        const isPlate = name === 'dePlaca';
        const isRenavam = name === 'deRenavam';

        if (isPlate || isRenavam) {
            const isFieldValid = await validateFieldValue(name, value);

            /*
             * 2.8.1.1 - INCLUSÃO DE VEÍCULO - PORTAL INTERNO - ANALISE DE SOLICITAÇÃO
             */
            if (isPlate && isInternalForm && isSituacaoAnalise && isInclusaoVeiculo) {
                await handleChangePlateAnaliseInclusao(target, setFieldValue, values.deRenavam, isFieldValid);
                return;
            }

            setLoadingDetran(true);
            let modifiedValues = { ...vehiclesInitialValues };

            if (isFieldValid) {
                if (
                    !(await validateVehicle(
                        intl,
                        service.cdServico,
                        request.cdSolicitacao,
                        vehicles,
                        indexActualVehicle,
                        name,
                        value,
                    ))
                ) {
                    setFieldValue(name, getLastValue(target));
                    setLoadingDetran(false);
                    return;
                }

                if (WITH_DETRAN) {
                    const isDetranData = foundDetran;
                    const oldValues = {};

                    DETRAN_KEYS.forEach((field) => (oldValues[field] = values?.[field]));

                    const result = await detranSearch(value);

                    if (Object.keys(result).length > 1) {
                        // result sempre terá foundOnDetran dentro dele
                        Object.keys(result).forEach((key) => {
                            setFieldValue(key, result[key]);
                            modifiedValues[key] = result[key];
                        });
                    } else {
                        DETRAN_KEYS.forEach((field) => setFieldValue(field, vehiclePayload[field] ?? ''));

                        setFieldValue('nmProprietario', oldValues['nmProprietario'] ?? '');
                        setFieldValue('deCpfCnpjProprietario', oldValues['deCpfCnpjProprietario'] ?? '');
                        setFieldValue(name, oldValues[name] ?? ''); //dePlaca/deRenavam

                        if (isPlate && !isDetranData) {
                            setFieldValue('deRenavam', oldValues['deRenavam'] ?? '');
                        }
                        if (isRenavam && !isDetranData) {
                            setFieldValue('dePlaca', oldValues['dePlaca'] ?? '');
                        }
                        if (!isDetranData) {
                            setFieldValue('deChassi', oldValues['deChassi'] ?? '');
                        }
                    }
                }
            }

            if (isPlate) {
                setLoadingCRLV(true);
                setLoadingDocuments(true);
                let same = true;

                if (vehicle && vehicle.content && vehicle.content.dePlaca && vehicle.content.dePlaca !== value) {
                    //plate changed
                    same = false;
                }

                if (same) {
                    const tempDocuments = [...documents].map((doc) => {
                        return { ...doc, dePlaca: isPlate ? value : modifiedValues['dePlaca'] };
                    });
                    setOthersDocuments(tempDocuments);
                    await dispatch(setFiles(tempDocuments));
                } else {
                    await resetFiles(value, setFieldValue, modifiedValues);
                }

                setLoadingDocuments(false);
                setLoadingCRLV(false);
            }
            setLoadingDetran(false);
        }
    };

    useEffect(() => {
        return () => {
            dispatch(clearFiles());
        };
    }, [dispatch]);

    useEffect(() => {
        const init = async () => {
            const tempFileData = _.cloneDeep(filesTableData);
            tempFileData.data = othersDocuments;
            setFilesTableData(tempFileData);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [othersDocuments]);

    useEffect(() => {
        (() => {
            if (vehiclesInitialValues?.documentos) {
                const temp = vehiclesInitialValues.documentos
                    .filter((doc) => {
                        return doc.cdTipoDocumento === DOCUMENT_TYPES.OTHERS;
                    })
                    .map((doc) => {
                        const t = { ...doc };
                        t.canEdit = canEdit;
                        if (readOnly) {
                            t.canEdit = false;
                        }
                        if (!t.canEdit) {
                            let description = '';
                            if (!t.deDescricao || t.deDescricao === '') {
                                if (t.nmTipoDocumento && t.nmTipoDocumento !== '') {
                                    description = t.nmTipoDocumento;
                                } else if (t.fileName && t.fileName !== '') {
                                    let nameSplitted = t.fileName.split('.');
                                    nameSplitted.pop();
                                    description = nameSplitted.join('');
                                }
                                t.deDescricao = description;
                            }
                        }
                        return t;
                    });
                setOthersDocuments(temp);
            }
        })();
    }, [canEdit, readOnly, vehiclesInitialValues]);

    useEffect(() => {
        (async () => {
            setLoadingDocuments(true);

            let docsOK = [];
            if (revisions && Object.keys(revisions).length > 0) {
                let lastKey = Object.keys(revisions).pop();
                const revision = revisions[lastKey];
                if (revision?.documentos && revision?.documentos.length > 0) {
                    if (vehiclesInitialValues.documentos && vehiclesInitialValues.documentos.length > 0) {
                        vehiclesInitialValues.documentos.forEach((file) => {
                            const thisDocument = _.find(revision.documentos, ['idFile', file.idFile]);
                            if (thisDocument?.boConformidade) {
                                docsOK.push(file.idFile);
                            }
                        });
                    }
                }
            }

            if (documents.length === 0 && vehiclesInitialValues.documentos) {
                const othersDocuments = vehiclesInitialValues.documentos
                    .filter((doc) => {
                        return doc.cdTipoDocumento === DOCUMENT_TYPES.OTHERS;
                    })
                    .map((doc) => {
                        const t = { ...doc };
                        if (readOnly) {
                            t.canEdit = false;
                        } else {
                            t.canEdit = canEdit;
                            if (canEdit && doc?.idFile) {
                                t.canEdit = !docsOK.includes(doc.idFile);
                            } else {
                                if (!canEdit && service.cdServico === SERVICES_CODES.EXCLUSAO_VEICULOS) {
                                    if (!request.situacao) {
                                        // new request
                                        t.canEdit = true;
                                    } else {
                                        t.canEdit =
                                            request.situacao?.boEdicaoPortalExterno ||
                                            request.cdSituacao === STATUS_CODES.EM_CONSTRUCAO;
                                    }
                                }
                            }
                        }

                        if (!t.canEdit) {
                            let description = '';
                            if (!t.deDescricao || t.deDescricao === '') {
                                if (t.nmTipoDocumento && t.nmTipoDocumento !== '') {
                                    description = t.nmTipoDocumento;
                                } else if (t.fileName && t.fileName !== '') {
                                    let nameSplitted = t.fileName.split('.');
                                    nameSplitted.pop();
                                    description = nameSplitted.join('');
                                }
                                t.deDescricao = description;
                            }
                        }

                        //PREFFIX
                        if (vehiclesInitialValues?.dePlaca && vehiclesInitialValues?.dePlaca !== '') {
                            t.dePlaca = vehiclesInitialValues.dePlaca;
                        }
                        return t;
                    });

                setOthersDocuments(othersDocuments);
                await dispatch(setFiles(othersDocuments));
            }
            if (idFile && docsOK.includes(idFile)) {
                setDisableEditCRLV(true);
            }
            setLoadingDocuments(false);
            setLoading(false);
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, vehiclesInitialValues, documents.length, canEdit, revisions, idFile]);

    useEffect(() => {
        if (request.cdServico === SERVICES_CODES.LAUDO_VISTORIA) {
            setHideAditionalDocs(true);
        } else if (SERVICES_GROUPS.LICENSE.includes(request.cdServico)) {
            setHideAditionalDocs(false);
        } else {
            if (disableEditCRLV || disableOtherDocs) {
                if (!vehiclesInitialValues.documentos || vehiclesInitialValues.documentos.length <= 1) {
                    setHideAditionalDocs(true);
                } else {
                    setHideAditionalDocs(false);
                }
            }
        }
    }, [disableEditCRLV, disableOtherDocs, request.cdServico, vehiclesInitialValues.documentos]);

    useEffect(() => {
        const init = async () => {
            if (SERVICES_GROUPS.LICENSE.includes(service.cdServico) ||
                    (SERVICES_CODES.VISTORIA === service.cdServico)) {
                setLoadingCRLV(true);
                setDocumentsList(vehicleDocuments);
                setLoadingCRLV(false);
            }
        };
        init();
    }, [service.cdServico, vehicleDocuments]);

    const findAndAssignValue = (valuesInt, key, data, property) => {
        if (valuesInt[key]) {
            valuesInt[key] = Number(valuesInt[key]);
            const found = data.find((item) => item[key] === valuesInt[key]);
            if (found) valuesInt[property] = found;
        }
    };

    const handleSave = async (values, survey) => {
        setSaving(true);
        const { cdServico } = service ?? {};
        const { cdSolicitacao } = request ?? {};
        if (!survey) {
            try {
                const [isValidInclusaoComplementar, isValidVehicle] = await Promise.all([
                    validaInclusaoVeiculoComplementar(
                        intl,
                        company,
                        cdServico,
                        cdSolicitacao,
                        vehicles,
                        indexActualVehicle,
                        subSystems,
                        values,
                    ),
                    validateVehicle(
                        intl,
                        cdServico,
                        cdSolicitacao,
                        vehicles,
                        indexActualVehicle,
                        'deRenavam',
                        values.deRenavam,
                    ),
                ]);
                if (!isValidInclusaoComplementar || !isValidVehicle) {
                    setSaving(false);
                    return;
                }
            } catch (error) {
                console.error('Error during validation:', error);
                setSaving(false);
                return;
            }

            // eslint-disable-next-line
            switch (cdServico) {
                case SERVICES_CODES.INCLUSAO_VEICULOS:
                case SERVICES_CODES.ALTERACAO_VEICULOS:
                    const cdEmpresa =
                        activeCompany?.cdEmpresa ?? request?.cdEmpresa ?? internalNewRequest?.company?.cdEmpresa;
                    const response = await companyEndpoints.bloquearCadastroVeiculosComplementares(cdEmpresa, values);
                    if (response?.data && vehicles.length >= vehiclesLimit) {
                        notification(intl.formatMessage({ id: 'MESSAGES.SOMENTE_UM_VEICULO_COMPLEMENTAR' }), {
                            toastId: toastIds.validacaoVeiculoComplementar,
                            type: 'error',
                        });
                        setSaving(false);
                        return;
                    }
            }

            if (documentsList.length > 0) {
                const validateDoc = validateDocumentation(values.documentos, documentsList);
                if (!validateDoc) {
                    notification('O envio de um documento é obrigatório.', {
                        toastId: 'DOC_VALIDATION',
                        type: 'error',
                    });
                    setSaving(false);
                    return;
                }
            }
            !!onSave && (await onSave(values, editing ? indexActualVehicle : null, canEditField));
            setSaving(false);
            return;
        }

        const valuesInt = { ...vehicle.content, ...values };

        if (valuesInt.deUtilizacao === 'Sistema') {
            const subSystem = _.find(subSystems, ['cdSubSistema', Number(valuesInt.cdSubSistema)]);
            const maxAge = await validateVehicleAge({
                service: service,
                plate: values.dePlaca,
                subSystem: canEditField ? subSystem : null,
                chassiYear: canEditField ? valuesInt.nuAnoFabricacao : null,
                deUtilizacao: canEditField ? valuesInt.deUtilizacao : null,
            });
            if (subSystem && maxAge) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.VEHICLE_MAX_AGE' }), {
                    toastId: 'MESSAGES.SERVICE.VEHICLE_MAX_AGE',
                    type: 'error',
                });
                onCloseDrawer();
                setSaving(false);
                return;
            }
        }

        if (valuesInt.deCpfCnpjProprietario)
            valuesInt.deCpfCnpjProprietario = helpers.somenteAlfanumericos(valuesInt.deCpfCnpjProprietario);

        findAndAssignValue(valuesInt, 'cdModeloVeiculo', vehicleModels, 'modelo');
        findAndAssignValue(valuesInt, 'cdCategoriaVeiculo', vehicleCategories, 'categoria');
        findAndAssignValue(valuesInt, 'cdClassificacaoVeiculo', vehicleClassification, 'classificacaoVeiculo');
        findAndAssignValue(valuesInt, 'cdSubSistema', subSystems, 'subSistema');
        findAndAssignValue(valuesInt, 'cdTipoAcessibilidade', accessibilityType, 'tipoAcessibilidade');
        findAndAssignValue(valuesInt, 'cdTipoCombustivel', fuelType, 'tipoCombustivel');
        findAndAssignValue(valuesInt, 'cdTipoVeiculo', vehicleType, 'tipoVeiculo');

        const response = await vehiclesEndpoint.saveVehicle(valuesInt);

        if (!response || response.status !== 200 || !response.data) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.VEHICLE_UPDATE_ERROR' }), {
                toastId: 'MESSAGES.SERVICE.VEHICLE_UPDATE_ERROR',
                type: 'error',
            });
            setSaving(false);
            return;
        }

        //UPDATE REQUEST DETAILS
        const updatedRequest = _.cloneDeep(request);
        const jsonSolicitacao = JSON.parse(updatedRequest.jsonSolicitacao);
        jsonSolicitacao.vistoriaVeiculos = jsonSolicitacao.vistoriaVeiculos.map((vv) => {
            const temp = { ...vv };
            if (temp.cdVeiculo === response.data.cdVeiculo) {
                return { ...temp,
                    veiculo: valuesInt,
                    boVeiculoNovo: valuesInt.boVeiculoNovo,
                    idVistoria: valuesInt.idVistoria,
                    dtVistoria: valuesInt.dtVistoria,
                    tpSituacao: valuesInt.tpSituacao,
                    cdMunicipioVistoria: valuesInt.cdMunicipioVistoria,
                    dtNotaFiscal: valuesInt.dtNotaFiscal,
                    nuValorBem: valuesInt.nuValorBem,
                };
            }
            return temp;
        });
        updatedRequest.jsonSolicitacao = JSON.stringify(jsonSolicitacao);
        //save request

        const reponseRequest = await requestEndpoints.saveRequest(updatedRequest, '');
        if (!reponseRequest?.status === 200 || !reponseRequest?.data) {
            notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.VEHICLE_UPDATE_ERROR' }), {
                toastId: 'MESSAGES.SERVICE.VEHICLE_UPDATE_ERROR',
                type: 'error',
            });
            setSaving(false);
            return;
        }
        await dispatch(updateRequestDetails(reponseRequest.data));
        onCloseDrawer();
        setSaving(false);
    };

    const requiredFieldsByService = useMemo(
        () => {
            return service.cdServico !== SERVICES_CODES.EXCLUSAO_VEICULOS ? requiredFields : {};
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [service.cdServico],
    );

    if (loading) {
        return <CircularProgress size={20} />;
    }

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={vehiclesInitialValues}
                validationSchema={addRequiredFields(
                    requiredFieldsByService,
                    validacoesVehicleForm,
                    isSurvey ? surveyValidationSchema : null)}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={() => {}}>
                {(formProps) => {
                    const { values, validateField } = formProps;
                    return (
                        <Form className={classes.containerForm}>
                            <ImperativeFormValues
                                imperativeFormControlsRef={imperativeFormControlsRef}
                                {...formProps}
                            />

                            <Grid container spacing={3}>
                                <OwnerOperator
                                    {...props}
                                    {...formProps}
                                    classes={classes}
                                    loadingDetran={loading || loadingDetran}
                                    foundDetran={foundDetran}
                                    foundOnDetran={foundOnDetran}
                                    campoDiferenteDoOriginal={campoDiferenteDoOriginal}
                                    highlightEnabled={highlightEnabled}
                                    isFieldDisabled={isFieldDisabled}
                                    requiredFields={requiredFields}
                                />

                                <VehicleData
                                    {...props}
                                    {...formProps}
                                    classes={classes}
                                    blockChangesField={blockChangesField}
                                    handleChangeField={handleChangeField}
                                    handleBlurField={handleBlurField}
                                    handleFocusField={handleFocusField}
                                    validateField={validateField}
                                    loadingDetran={loadingDetran}
                                    foundDetran={foundDetran}
                                    foundOnDetran={foundOnDetran}
                                    campoDiferenteDoOriginal={campoDiferenteDoOriginal}
                                    highlightEnabled={highlightEnabled}
                                    isFieldDisabled={isFieldDisabled}
                                    requiredFields={requiredFields}
                                />

                                <Grid item xs={12}>
                                    <Field
                                        name="deObservacao"
                                        label={`Observações${labelObrigatoriedade('deObservacao', requiredFields)}`}
                                        variant="filled"
                                        margin="dense"
                                        disabled={isFieldDisabled('deObservacao')}
                                        multiline
                                        minRows={4}
                                        fullWidth
                                        as={HighlightableTextField}
                                        safeToHighlight={!loadingDetran}
                                        isHighlightedFunction={campoDiferenteDoOriginal('deObservacao')}
                                        inputProps={{
                                            maxLength: 4800,
                                        }}
                                    />
                                </Grid>

                                <AdditionalData
                                    {...props}
                                    {...formProps}
                                    loadingDetran={loadingDetran || loading}
                                    foundDetran={foundDetran}
                                    foundOnDetran={foundOnDetran}
                                    handleChangeField={handleChangeField}
                                    handleBlurField={handleBlurField}
                                    campoDiferenteDoOriginal={campoDiferenteDoOriginal}
                                    highlightEnabled={highlightEnabled}
                                    isFieldDisabled={isFieldDisabled}
                                    requiredFields={requiredFields}
                                />
                            </Grid>

                            {isSurvey && (
                                <Grid container spacing={3}>
                                    <AdditionalDataSurvey
                                        {...props}
                                        {...formProps}
                                    />
                                </Grid>
                            )}

                            {!isInternalForm && values.dePlaca && values.dePlaca !== '' && (
                                <Grid container spacing={3} style={{ alignItems: 'flex-end' }}>
                                    <Grid item xs={12}>
                                        <h3 className={classes.subtitle}>Documentação</h3>
                                    </Grid>

                                    {loadingCRLV && (
                                        <div className={classes.loadingCRLV}>
                                            <CircularProgress size={20} />
                                        </div>
                                    )}
                                    {!loadingCRLV && (
                                        <>
                                            {documentsList.length === 0 && (
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Field
                                                        value={fileName ? fileName : ''}
                                                        required={requiredFields?.documentos}
                                                        name={fileName ? fileName : `${values.dePlaca}-CRLV`}
                                                        label={'CRLV'}
                                                        id={DOCUMENT_TYPES.CRLV}
                                                        disabled={disableEditCRLV}
                                                        highlighted={highlightEnabled && CRLVAlterada}
                                                        idGdoc={idGdoc}
                                                        idFile={idFile}
                                                        onChangeFile={(doc) => {
                                                            handleChangeFile(doc, DOCUMENT_TYPES.CRLV, values);
                                                            setUpLoading(false);
                                                        }}
                                                        onDeleteFile={(idFile) => handleDeleteFile(idFile, values)}
                                                        uploading={uploading}
                                                        setUpLoading={setUpLoading}
                                                        component={DocumentField}
                                                        error={formProps.errors?.documentos}
                                                    />
                                                </Grid>
                                            )}

                                            {documentsList.length > 0 && (
                                                <AdditionalFiles
                                                    {...props}
                                                    {...formProps}
                                                    readOnly={readOnly}
                                                    documentsList={documentsList}
                                                    dePlaca={values.dePlaca}
                                                    disabled={disableEditCRLV}
                                                    isSurvey={isSurvey}
                                                />
                                            )}

                                            {!hideAditionalDocs && (
                                                <Grid item xs={12}>
                                                    {loadingDocuments && <CircularProgress size={16} />}
                                                    {!loadingDocuments && (
                                                        <GrowingTable
                                                            title="Outros"
                                                            tabledata={filesTableData}
                                                            addtabledata={addFiles}
                                                            handlechange={onChangeField}
                                                            handleblur={onBlurField}
                                                            customAddElement={customAddDocuments}
                                                            loading={loadingDocuments}
                                                            canEdit={!disableOtherDocs}
                                                            blockadd={uploading}
                                                        />
                                                    )}
                                                </Grid>
                                            )}
                                        </>
                                    )}
                                </Grid>
                            )}

                            {saving ? (
                                <Grid xs={12} style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>
                                    <CircularProgress size={16} />
                                </Grid>
                            ) : (
                                <SaveButton
                                    mainColor={mainColor}
                                    hideActionBtn={!showSaveButton}
                                    isInternalForm={isInternalForm}
                                    canEdit={canEdit}
                                    onCancel={() => {
                                        !!onCancel && onCancel(values);
                                    }}
                                    onSave={() => {
                                        validateFormikForm(
                                            formProps,
                                            addRequiredFields(
                                                requiredFieldsByService,
                                                validacoesVehicleForm,
                                            isSurvey ? surveyValidationSchema : null),
                                        ).then((res) => {
                                            if (Object.keys(res).length > 0) {
                                                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                    toastId: 'VALIDATION',
                                                    type: 'error',
                                                });
                                                return;
                                            }
                                            if (
                                                service.cdServico === SERVICES_CODES.VISTORIA &&
                                                request.cdSituacao === STATUS_CODES.EM_ANALISE
                                            ) {
                                                handleSave(values, true);
                                            } else {
                                                handleSave(values);
                                            }
                                        });
                                    }}
                                />
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
}
export default injectIntl(VehicleForm);
